import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Paper,
  IconButton,
  Container,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import { useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import StyleIcon from '@mui/icons-material/Style';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { request } from '../../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { Category } from '../../../types';

const CategoryInfo: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const res = await request({
          url: `category/get-category/${id}`,
          method: 'get',
        });

        if (res.status === 'success' && res.category) {
          setCategory(res.category);
        } else {
          toast.error('Category not found');
          navigate('/configuration/categories');
        }
      } catch (error) {
        console.error('Error fetching category:', error);
        toast.error('Failed to load category details');
        navigate('/configuration/categories');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCategory();
    }
  }, [id, navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading category details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!category) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" color="error">
            Category not found
          </Typography>
        </Box>
      </Container>
    );
  }

  const getAttributeTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      select: '#1976d2',
      number: '#2e7d32',
      string: '#ed6c02',
      boolean: '#9c27b0',
      date: '#d32f2f',
    };
    return colors[type] || '#757575';
  };

  const getAttributeTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      select: 'Select',
      number: 'Number',
      string: 'Text',
      boolean: 'Boolean',
      date: 'Date',
    };
    return labels[type] || type;
  };

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
        <Chip
          onClick={() => navigate(-1)}
          icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
          label="Back"
          variant="filled"
          color="secondary"
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>
      {/* First Section: Category Name and ID with Edit Icon */}
      <Card elevation={3} sx={{ borderRadius: 1, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <CategoryIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" fontWeight={700} color="black">
                    {category.name}
                  </Typography>
                  
                  {/* Alias Names Display */}
                  {/* {category.aliasNames && category.aliasNames.length > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 1 }}>
                      <AlternateEmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {category.aliasNames.join(', ')}
                      </Typography>
                    </Stack>
                  )} */}

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: category.aliasNames && category.aliasNames.length > 0 ? 0 : 1 }}>
                    <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Category ID:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {category._id}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Add Product for this Car">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/inventory/addProduct', {
                    state: {
                      categoryId: category._id,
                      source: 'category'
                    }
                  })}
                  size="small"
                >
                  Add Product
                </Button>
              </Tooltip>
              <Tooltip title="Edit Category">
                <IconButton
                  onClick={() => navigate(`/configuration/categories/editCategory/${category._id}`)}
                  sx={{
                    color: 'primary.main',
                    backgroundColor: 'primary.50',
                    '&:hover': {
                      backgroundColor: 'primary.100',
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>

          </Stack>
        </CardContent>
      </Card>

      {/* Second Section: Description and Attributes */}
      <Card elevation={3} sx={{ borderRadius: 1, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={4}>
             <Box sx={{display: 'flex', gap:2}}>
            {/* Description */}
            <Box minWidth={"50%"}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <DescriptionIcon sx={{ color: 'black' }} />
                <Typography variant="h6" fontWeight={600} color="black">
                  Description
                </Typography>
              </Stack>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: 'grey.50',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="body1">
                  {category.description || 'No description provided'}
                </Typography>
              </Paper>
            </Box>

            <Divider />

            {/* Alias Names Section (if available) */}
            {category.aliasNames && category.aliasNames.length > 0 && (
              <>
                <Box minWidth={"50%"}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <AlternateEmailIcon sx={{ color: 'black' }} />
                    <Typography variant="h6" fontWeight={600} color="black">
                      Alias Names
                    </Typography>
                    <Chip
                      label={`${category.aliasNames.length} alias(es)`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Stack>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: 'grey.50',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {category.aliasNames.map((alias, index) => (
                        <Chip
                          key={index}
                          label={alias}
                          color="secondary"
                          variant="filled"
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                </Box>
                <Divider />
              </>
            )}

           </Box>

            {/* Attributes - Three Column Layout using Stack and Box */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <StyleIcon sx={{ color: 'black' }} />
                <Typography variant="h6" fontWeight={600} color="black">
                  Attributes
                </Typography>
                <Chip
                  label={`${category.attributesTemplate.length} attributes`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>

              {category.attributesTemplate.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'flex-start',
                    alignItems: 'stretch'
                  }}
                >
                  {category.attributesTemplate.map((attribute) => (
                    <Box
                      key={attribute._id}
                      sx={{
                        width: { xs: '100%', md: 'calc(33.333% - 11px)' }, // Adjusted for gap
                        minWidth: { md: 'calc(33.333% - 11px)' }
                      }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 1,
                          borderColor: 'grey.200',
                          backgroundColor: 'white',
                          height: '100%',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        <Stack spacing={2} height="100%">
                          {/* Attribute Header */}
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} color="black" gutterBottom>
                              {attribute.label}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Key:
                              </Typography>
                              <Chip
                                label={attribute.key}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  fontSize: '0.7rem',
                                  height: 24,
                                }}
                              />
                            </Stack>
                            <Chip
                              label={getAttributeTypeLabel(attribute.type)}
                              size="small"
                              sx={{
                                backgroundColor: getAttributeTypeColor(attribute.type),
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 24,
                              }}
                            />
                          </Box>

                          {/* Options */}
                          {attribute.options.length > 0 && (
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" gutterBottom>
                                Options:
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {attribute.options.map((option, index) => (
                                  <Chip
                                    key={index}
                                    label={option}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 22,
                                      mb: 0.5,
                                      borderColor: 'primary.main',
                                      color: 'primary.main',
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 1,
                    textAlign: 'center',
                    backgroundColor: 'grey.50',
                    borderColor: 'grey.200'
                  }}
                >
                  <StyleIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    No attributes defined for this category
                  </Typography>
                </Paper>
              )}
            </Box>

          </Stack>
        </CardContent>
      </Card>

      {/* Third Section: System Information */}
      <Card elevation={3} sx={{ borderRadius: 1 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <CalendarTodayIcon sx={{ color: 'black' }} />
            <Typography variant="h6" fontWeight={600} color="black">
              System Information
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                  Created Date
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                  {new Date(category.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                  Last Updated
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                  {new Date(category.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};

export default CategoryInfo;